/**
 * Component: sa-mobile-nav-metaobject
 * Description: Mobile navigation using Metaobjects - 4-level page-based drilldown with slide animation
 */
if (!customElements.get('sa-mobile-nav-metaobject')) {
  class SaMobileNavMetaobject extends HTMLElement {
    constructor() {
      super();
      this._historyStack = ['l1'];
      this._isAnimating = false;
      this._animationDuration = 300;

      this.handleItemClick = this.handleItemClick.bind(this);
      this.handleBackClick = this.handleBackClick.bind(this);
      this.handleLinkClick = this.handleLinkClick.bind(this);
      this.updateAnnouncementOffset = this.updateAnnouncementOffset.bind(this);
      this.handleDrawerOpen = this.handleDrawerOpen.bind(this);
    }

    connectedCallback() {
      this._pages = this.querySelectorAll('.sa-mobile-nav-metaobject__page');

      // Navigate-forward items (buttons with data-navigate-to)
      const navigateItems = this.querySelectorAll('.sa-mobile-nav-metaobject__page-item[data-navigate-to]');
      navigateItems.forEach(item => {
        item.addEventListener('click', this.handleItemClick);
      });

      // Back buttons
      const backButtons = this.querySelectorAll('.sa-mobile-nav-metaobject__page-back');
      backButtons.forEach(btn => {
        btn.addEventListener('click', this.handleBackClick);
      });

      // Direct link items (anchor tags that should close drawer after navigation)
      const linkItems = this.querySelectorAll('.sa-mobile-nav-metaobject__page-item--link');
      linkItems.forEach(link => {
        link.addEventListener('click', this.handleLinkClick);
      });

      // Listen for drawer open to reset navigation state
      this._setupDrawerObserver();

      this.updateAnnouncementOffset();
      setTimeout(this.updateAnnouncementOffset, 100);
      window.addEventListener('resize', this.updateAnnouncementOffset);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.updateAnnouncementOffset);
      if (this._drawerObserver) {
        this._drawerObserver.disconnect();
      }
    }

    /**
     * Navigate forward to a target page
     */
    handleItemClick(e) {
      e.preventDefault();
      if (this._isAnimating) return;

      const targetId = e.currentTarget.getAttribute('data-navigate-to');
      if (!targetId) return;

      this._navigateForward(targetId);
    }

    /**
     * Navigate back to the previous page
     */
    handleBackClick(e) {
      e.preventDefault();
      if (this._isAnimating) return;

      this._navigateBack();
    }

    /**
     * Handle direct link click - close drawer after a small delay
     * to allow the link navigation to start
     */
    handleLinkClick(e) {
      // Let the default anchor behavior happen, then close drawer
      setTimeout(() => {
        if (window.theme && window.theme.NavDrawer) {
          window.theme.NavDrawer.close();
        }
      }, 100);
    }

    /**
     * Navigate forward: push target to history, animate pages
     */
    _navigateForward(targetId) {
      if (this._isAnimating) return;
      this._isAnimating = true;

      const currentId = this._historyStack[this._historyStack.length - 1];
      const currentPage = this.querySelector(`.sa-mobile-nav-metaobject__page[data-page-id="${currentId}"]`);
      const targetPage = this.querySelector(`.sa-mobile-nav-metaobject__page[data-page-id="${targetId}"]`);

      if (!targetPage) {
        this._isAnimating = false;
        return;
      }

      // Push to history
      this._historyStack.push(targetId);

      // Prepare target page
      targetPage.classList.add('sa-mobile-nav-metaobject__page--active');

      // Force reflow
      targetPage.offsetHeight;

      // Animate current page out to left
      if (currentPage) {
        currentPage.classList.add('sa-mobile-nav-metaobject__page--leaving');
        currentPage.classList.remove('sa-mobile-nav-metaobject__page--active');
      }

      // Cleanup after animation
      setTimeout(() => {
        if (currentPage) {
          currentPage.classList.remove('sa-mobile-nav-metaobject__page--leaving');
        }
        this._isAnimating = false;
      }, this._animationDuration);
    }

    /**
     * Navigate back: pop from history, animate pages back
     */
    _navigateBack() {
      if (this._isAnimating) return;
      if (this._historyStack.length <= 1) return;

      this._isAnimating = true;

      const currentId = this._historyStack.pop();
      const previousId = this._historyStack[this._historyStack.length - 1];

      const currentPage = this.querySelector(`.sa-mobile-nav-metaobject__page[data-page-id="${currentId}"]`);
      const previousPage = this.querySelector(`.sa-mobile-nav-metaobject__page[data-page-id="${previousId}"]`);

      if (!previousPage) {
        this._isAnimating = false;
        return;
      }

      // Show previous page
      previousPage.classList.add('sa-mobile-nav-metaobject__page--active');

      // Force reflow
      previousPage.offsetHeight;

      // Animate current page out to right
      if (currentPage) {
        currentPage.classList.add('sa-mobile-nav-metaobject__page--back-leaving');
        currentPage.classList.remove('sa-mobile-nav-metaobject__page--active');
      }

      // Cleanup after animation
      setTimeout(() => {
        if (currentPage) {
          currentPage.classList.remove('sa-mobile-nav-metaobject__page--back-leaving');
        }
        this._isAnimating = false;
      }, this._animationDuration);
    }

    /**
     * Reset navigation to level 1 page
     */
    reset() {
      // Deactivate all pages
      this._pages.forEach(page => {
        page.classList.remove(
          'sa-mobile-nav-metaobject__page--active',
          'sa-mobile-nav-metaobject__page--leaving',
          'sa-mobile-nav-metaobject__page--back-leaving'
        );
      });

      // Activate level 1 page
      const l1Page = this.querySelector('.sa-mobile-nav-metaobject__page[data-page-id="l1"]');
      if (l1Page) {
        l1Page.classList.add('sa-mobile-nav-metaobject__page--active');
      }

      // Reset history
      this._historyStack = ['l1'];
      this._isAnimating = false;
    }

    /**
     * Observe drawer open/close to reset navigation state
     */
    _setupDrawerObserver() {
      const drawer = document.getElementById('NavDrawer');
      if (!drawer) return;

      // Observe class changes on the drawer
      this._drawerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (drawer.classList.contains('drawer--is-open')) {
              // Drawer opened - reset navigation to level 1
              this.handleDrawerOpen();
            }
          }
        });
      });

      this._drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    /**
     * Handle drawer open: reset navigation to level 1
     */
    handleDrawerOpen() {
      // Reset after a small delay to allow drawer animation to start
      setTimeout(() => {
        this.reset();
      }, 50);
    }

    /**
     * Update announcement bar height CSS variable for drawer positioning
     */
    updateAnnouncementOffset() {
      try {
        const bar = document.querySelector('.announcement-bar');
        let h = 0;
        if (bar) {
          const rect = bar.getBoundingClientRect();
          h = Math.max(0, Math.round(rect.height || bar.offsetHeight || 0));
        }
        document.documentElement.style.setProperty('--announcement-bar-height', `${h}px`);
      } catch (e) {}
    }
  }

  customElements.define('sa-mobile-nav-metaobject', SaMobileNavMetaobject);
}
